import { Building2, ClipboardList } from "lucide-react";
import type { JSX } from "react";

import { DetailTabs, type DetailTab } from "@crm-src/components/detail-tabs";

/**
 * The two tabs of CRM ▸ Reseller Management (2026-09-12):
 *
 *   - **Resellers** — the companies in the network (the table + its filters,
 *     create / edit / delete, import / export).
 *   - **Reseller Applications** — the companies asking to JOIN it (review,
 *     countersign, Activate / Reject). Moved here from System Admin so the
 *     person who activates a reseller finds the queue where they already
 *     look. Rendered ONLY for a holder of `reseller_applications:access`
 *     (the dashboard's `crm:reseller_applications` grant, super_admin
 *     baseline) — for anyone else the strip has one tab, and it still renders
 *     so the page reads the same whichever role opens it.
 *
 * The count pill on the second tab is the SIGN that something is waiting on
 * a decision: the number of OPEN applications (registered or documents in),
 * omitted entirely at zero. The page owns the query (and disables it for an
 * actor without the grant); this strip only renders what it is handed.
 */
export const RESELLER_MANAGEMENT_TAB_IDS = ["resellers", "applications"] as const;
export type ResellerManagementTabId = (typeof RESELLER_MANAGEMENT_TAB_IDS)[number];

interface ResellerManagementTabsProps {
  activeId: ResellerManagementTabId;
  onTabChange: (id: ResellerManagementTabId) => void;
  /** Whether the actor may see the applications tab at all. */
  showApplications: boolean;
  /** Open applications awaiting a decision; 0 (or unknown) shows no pill. */
  openApplicationCount: number;
}

export function ResellerManagementTabs({
  activeId,
  onTabChange,
  showApplications,
  openApplicationCount,
}: ResellerManagementTabsProps): JSX.Element {
  const tabs: DetailTab[] = [
    { id: "resellers", label: "Resellers", icon: Building2 },
    ...(showApplications
      ? [
          {
            id: "applications",
            label: "Reseller Applications",
            icon: ClipboardList,
            badge: openApplicationCount > 0 ? openApplicationCount : null,
          },
        ]
      : []),
  ];
  return (
    <DetailTabs
      tabs={tabs}
      activeId={activeId}
      onTabChange={(id) => onTabChange(id as ResellerManagementTabId)}
      ariaLabel="Reseller management sections"
    />
  );
}
